import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { useAuth } from '@/context/AuthContext';
import api from '@/utils/api';
import { toast } from 'sonner';
import { 
  CalendarCheck, ChevronRight, Clock, User, Mail, Phone, FileText,
  CheckCircle, AlertCircle, Calendar, ChevronLeft, ChevronDown, UserCheck
} from 'lucide-react';

export default function Appointment() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const preSelectedService = searchParams.get('service') || '';

  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const [form, setForm] = useState({
    service_type: preSelectedService,
    full_name: '',
    email: '',
    phone: '',
    date: '',
    time_slot: '',
    document_number: '',
    notes: '',
  });

  // Pre-fill form with authenticated user data
  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        full_name: user.full_name || prev.full_name,
        email: user.email || prev.email,
        phone: user.phone || prev.phone,
        document_number: user.passport_number || user.document_number || prev.document_number,
      }));
    }
  }, [user]);

  // Get available dates (next 30 days, weekdays only)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const day = date.getDay();
      if (day !== 0 && day !== 6) { // Exclude weekends
        dates.push(date.toISOString().split('T')[0]);
      }
    }
    return dates;
  };

  const availableDates = getAvailableDates();

  useEffect(() => {
    api.get('/appointments/services').then(r => setServices(r.data || [])).catch(console.error);
  }, []);

  useEffect(() => {
    if (form.date) {
      api.get(`/appointments/available-slots?date=${form.date}`)
        .then(r => setAvailableSlots(r.data.available_slots || []))
        .catch(console.error);
    }
  }, [form.date]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (field === 'date') {
      setForm(prev => ({ ...prev, time_slot: '' }));
    }
  };

  const selectedService = services.find(s => s.id === form.service_type);

  const canProceed = () => {
    switch (step) {
      case 1: return !!form.service_type;
      case 2: return form.full_name && form.email && form.phone;
      case 3: return form.date && form.time_slot;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post('/appointments', form);
      setConfirmation(res.data);
      toast.success('Agendamento confirmado!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao criar agendamento');
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-lg mx-auto px-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Agendamento Confirmado!</h1>
            <p className="text-gray-500 mb-6">O seu agendamento foi registado com sucesso.</p>
            
            <div className="bg-gray-50 rounded-lg p-4 text-left mb-6">
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Referência:</span>
                  <span className="font-bold text-[#CC0000]">{confirmation.reference_code}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Serviço:</span>
                  <span className="font-medium">{confirmation.service_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Data:</span>
                  <span className="font-medium">{new Date(confirmation.date).toLocaleDateString('pt', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Hora:</span>
                  <span className="font-medium">{confirmation.time_slot}</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left mb-6">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold mb-1">Lembre-se:</p>
                  <ul className="list-disc list-inside space-y-1 text-amber-700">
                    <li>Apresente-se 15 minutos antes da hora marcada</li>
                    <li>Traga todos os documentos necessários em original e cópia</li>
                    <li>Um email de confirmação foi enviado para {confirmation.email}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <Link to="/" className="w-full py-2.5 bg-[#CC0000] text-white font-medium rounded-md hover:bg-[#A30000] transition-colors">
                Voltar ao Início
              </Link>
              <Link to="/servicos-consulares" className="w-full py-2.5 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors">
                Ver Outros Serviços
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <Link to="/" className="hover:text-white">Início</Link>
            <ChevronRight size={12} />
            <span className="text-white">Agendamento Online</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Agendamento Online</h1>
          <p className="text-gray-400 max-w-2xl">
            Marque a sua consulta na Embaixada de Angola em Espanha de forma rápida e conveniente.
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: 'Serviço' },
              { num: 2, label: 'Dados Pessoais' },
              { num: 3, label: 'Data e Hora' },
              { num: 4, label: 'Confirmação' },
            ].map(({ num, label }) => (
              <div key={num} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= num ? 'bg-[#CC0000] text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > num ? <CheckCircle size={16} /> : num}
                </div>
                <span className={`text-sm font-medium hidden sm:block ${step >= num ? 'text-gray-900' : 'text-gray-400'}`}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Step 1: Select Service */}
        {step === 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="step-service">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Selecione o Serviço</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {services.map(svc => (
                <button
                  key={svc.id}
                  onClick={() => handleChange('service_type', svc.id)}
                  data-testid={`service-${svc.id}`}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    form.service_type === svc.id 
                      ? 'border-[#CC0000] bg-red-50' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold text-gray-900">{svc.name}</div>
                  <div className="text-sm text-gray-500 mt-1 flex items-center gap-3">
                    <span className="flex items-center gap-1"><Clock size={12} /> {svc.duration} min</span>
                    <span className="text-[#CC0000] font-medium">€{svc.fee?.toFixed(2)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Personal Info */}
        {step === 2 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="step-personal">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Dados Pessoais</h2>
            
            {/* User authenticated notice */}
            {user && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                <UserCheck size={18} className="text-green-600" />
                <span className="text-sm text-green-800">
                  Os seus dados foram preenchidos automaticamente a partir do seu perfil.
                </span>
              </div>
            )}
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome Completo *</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={form.full_name}
                    onChange={e => handleChange('full_name', e.target.value)}
                    placeholder="O seu nome completo"
                    data-testid="input-fullname"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <div className="relative">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => handleChange('email', e.target.value)}
                    placeholder="seu@email.com"
                    data-testid="input-email"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefone *</label>
                <div className="relative">
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={e => handleChange('phone', e.target.value)}
                    placeholder="+34 600 000 000"
                    data-testid="input-phone"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Número do Documento (Passaporte/BI)</label>
                <div className="relative">
                  <FileText size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={form.document_number}
                    onChange={e => handleChange('document_number', e.target.value)}
                    placeholder="Opcional"
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Date and Time */}
        {step === 3 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="step-datetime">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Selecione Data e Hora</h2>
            
            {/* Date Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Data *</label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {availableDates.slice(0, 10).map(date => {
                  const d = new Date(date);
                  const isSelected = form.date === date;
                  return (
                    <button
                      key={date}
                      onClick={() => handleChange('date', date)}
                      data-testid={`date-${date}`}
                      className={`p-3 rounded-lg border-2 text-center transition-all ${
                        isSelected ? 'border-[#CC0000] bg-red-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-xs text-gray-500">{d.toLocaleDateString('pt', { weekday: 'short' })}</div>
                      <div className="text-lg font-bold text-gray-900">{d.getDate()}</div>
                      <div className="text-xs text-gray-400">{d.toLocaleDateString('pt', { month: 'short' })}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Time Selection */}
            {form.date && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hora *</label>
                {availableSlots.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Clock size={32} className="mx-auto mb-2 text-gray-300" />
                    <p>Nenhum horário disponível para esta data.</p>
                    <p className="text-sm">Por favor selecione outra data.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {availableSlots.map(slot => {
                      const isSelected = form.time_slot === slot;
                      return (
                        <button
                          key={slot}
                          onClick={() => handleChange('time_slot', slot)}
                          data-testid={`time-${slot}`}
                          className={`p-2.5 rounded-lg border-2 text-center font-medium transition-all ${
                            isSelected ? 'border-[#CC0000] bg-red-50 text-[#CC0000]' : 'border-gray-200 hover:border-gray-300 text-gray-700'
                          }`}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Notas Adicionais</label>
              <textarea
                value={form.notes}
                onChange={e => handleChange('notes', e.target.value)}
                placeholder="Informações adicionais relevantes para a sua consulta..."
                rows={3}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#CC0000] focus:border-[#CC0000] outline-none resize-none"
              />
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-testid="step-confirm">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Confirme o Seu Agendamento</h2>
            
            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Resumo do Agendamento</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Serviço:</span>
                    <span className="font-medium">{selectedService?.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Data:</span>
                    <span className="font-medium">{form.date && new Date(form.date).toLocaleDateString('pt', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Hora:</span>
                    <span className="font-medium">{form.time_slot}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Duração:</span>
                    <span className="font-medium">{selectedService?.duration} minutos</span>
                  </div>
                  {selectedService?.fee > 0 && (
                    <div className="flex justify-between pt-2 border-t border-gray-200">
                      <span className="text-gray-500">Taxa do serviço:</span>
                      <span className="font-bold text-[#CC0000]">€{selectedService?.fee?.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-800 mb-3">Dados Pessoais</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Nome:</span>
                    <span className="font-medium">{form.full_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Email:</span>
                    <span className="font-medium">{form.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Telefone:</span>
                    <span className="font-medium">{form.phone}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-2">
                <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  Ao confirmar, receberá um email com os detalhes do agendamento. 
                  Por favor apresente-se na Embaixada 15 minutos antes da hora marcada.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <button
            onClick={() => setStep(s => s - 1)}
            disabled={step === 1}
            className="flex items-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} /> Anterior
          </button>

          {step < 4 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#CC0000] text-white font-medium rounded-md hover:bg-[#A30000] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Próximo <ChevronRight size={16} />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="confirm-appointment"
              className="flex items-center gap-2 px-6 py-2.5 bg-[#CC0000] text-white font-medium rounded-md hover:bg-[#A30000] disabled:opacity-50 transition-colors"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  A processar...
                </>
              ) : (
                <>
                  <CheckCircle size={16} /> Confirmar Agendamento
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
